import type {TFunction} from 'i18next';
import {NodeType} from 'crqlar-react-common/dist/models/automation/automation';
import type {FlowPreview, PreviewFanOut, PreviewMember} from 'crqlar-react-common/dist/models/automation/flowPreview.model';
import {IfConditionGroup, readConditionGroups} from '../config/ifConditions';
import {conditionRuleDescriber} from '../components/IfConditionSummary';
import {formatWaitDuration} from '../utils/waitDuration';
import {entriesAt, isSendStep, memberName, PreviewEntry, withOutcome} from './flowPreviewModel';
import {reasonText} from './previewText';

export interface ListRow {
    name: string;
    /** The email address, or why the step stops, skips or fails for this person. */
    detail?: string;
    problem?: boolean;
    /** The same person more than once — a team member once per reservation. */
    times?: number;
    /** yyyy-MM-dd */
    arrival?: string;
    departure?: string;
    room?: string;
    language?: string;
}

const guestOf = (member?: PreviewMember): Pick<ListRow, 'arrival' | 'departure' | 'room' | 'language'> => ({
    arrival: member?.arrival,
    departure: member?.departure,
    room: member?.room,
    language: member?.language,
});

export interface StepList {
    title: string;
    /** How many the title says: entries, or for an Audience / Team members step the people. */
    count: number;
    /** The one problem every row shares, said once under the title instead of on every row. */
    reason?: string;
    rows: ListRow[];
}

/** `query` must already be lower-cased. */
export const matchesSearch = (row: ListRow, query: string): boolean => !query || [row.name, row.detail, row.room]
    .some(value => value?.toLocaleLowerCase().includes(query));

/** Rows without the column's value go last in either direction; ties sort by name. */
export const sortRows = <Row extends ListRow>(rows: Row[], field: string, descending: boolean, collator: Intl.Collator): Row[] => {
    const valueOf = (row: ListRow): string | undefined => {
        switch (field) {
            case 'detail': return row.detail;
            case 'stay': return row.arrival;
            case 'room': return row.room;
            case 'language': return row.language?.toLowerCase();
            default: return row.name;
        }
    };
    return [...rows].sort((a, b) => {
        const left = valueOf(a);
        const right = valueOf(b);
        if (!left || !right) return left === right ? collator.compare(a.name, b.name) : left ? -1 : 1;
        const order = field === 'stay' ? left.localeCompare(right) : collator.compare(left, right);
        return (descending ? -order : order) || collator.compare(a.name, b.name);
    });
};

export const detailTitleOf = (rows: ListRow[], t: (key: string) => string): string => {
    const isEmail = (row: ListRow) => !row.problem && !!row.detail?.includes('@');
    const withEmail = rows.some(isEmail);
    const withReason = rows.some(row => !!row.detail && !isEmail(row));
    return withEmail && withReason ? t('Email / reason') : withReason ? t('Reason') : t('Email');
};

/** A rule of a Continue if by its path from the API — `"0/r1"`, `"0/g0/r2"` — or null. */
const ruleAt = (groups: IfConditionGroup[], path: string) => {
    const [box, ...rest] = path.split('/');
    let group: IfConditionGroup | undefined = groups[Number(box)];
    for (const segment of rest) {
        if (!group) return null;
        if (segment.startsWith('g')) group = group.groups?.[Number(segment.slice(1))];
        else if (segment.startsWith('r')) return group.rules?.[Number(segment.slice(1))] ?? null;
    }
    return null;
};

const fanOutTitle = (fanOut: PreviewFanOut, t: TFunction): string => {
    switch (fanOut.memberType) {
        case 'USER':
            return t('In the team');
        case 'SURVEY_INVITATION':
            return t('Reminders due');
        default:
            return t('In the audience');
    }
};

/** One row per person: the same name and detail twice is one row that says how often. */
const collapse = (rows: ListRow[]): ListRow[] => {
    const byKey = new Map<string, ListRow>();
    for (const row of rows) {
        const key = `${row.name}\u0000${row.detail ?? ''}\u0000${row.problem ? 1 : 0}`;
        const seen = byKey.get(key);
        if (seen) seen.times = (seen.times ?? 1) + 1;
        else byKey.set(key, {...row});
    }
    return Array.from(byKey.values());
};

interface StepListsInput {
    preview: FlowPreview;
    nodeId: string;
    type: NodeType;
    config?: Record<string, any>;
    /** The step's own name, for a once-run row that has no guest. */
    label: string;
    t: TFunction;
    collator: Intl.Collator;
}

/** The lists behind a card's number, plus a note the step needs on top (a wait, an AI decision). */
export const stepLists = ({preview, nodeId, type, config, label, t, collator}: StepListsInput): {lists: StepList[]; note: string | null} => {
    const entries = entriesAt(preview, nodeId);
    const lists: StepList[] = [];
    const nameOf = (entry: PreviewEntry) => memberName(entry.member) || t('Unknown guest');

    const add = (title: string, rows: ListRow[], count = rows.length) => {
        if (!rows.length) return;
        const shared = rows[0].problem && rows.length > 1 && rows.every(row => row.detail === rows[0].detail)
            ? rows[0].detail : undefined;
        lists.push({
            title,
            count,
            reason: shared,
            rows: collapse(shared ? rows.map(row => ({...row, detail: undefined, problem: undefined})) : rows)
                .sort((a, b) => collator.compare(a.name, b.name)),
        });
    };

    const fanOut = preview.fanOuts.find(entry => entry.nodeId === nodeId);
    if (fanOut) {
        const rows = preview.members
            .filter(member => member.fanOutNodeId === nodeId)
            .map(member => ({name: memberName(member) || t('Unknown guest'), detail: member.email, ...guestOf(member)}));
        add(fanOutTitle(fanOut, t), rows, collapse(rows).length);
    } else if (type === NodeType.CONTINUE_IF) {
        const groups = readConditionGroups(config);
        const {describeRule} = conditionRuleDescriber(groups, t);
        // Who a filter stops is what it is there for, so they come first.
        add(t('Stop here'), withOutcome(entries, 'STOPPED').map(entry => ({
            name: nameOf(entry),
            ...guestOf(entry.member),
            detail: (entry.step.unmetRules ?? [])
                .map(path => {
                    if (path === 'limit') return t('Not met: {{rule}}', {rule: t('Send limit')});
                    const rule = ruleAt(groups, path);
                    return rule ? t('Not met: {{rule}}', {rule: describeRule(rule)}) : '';
                })
                .filter(Boolean)
                .join(' · '),
        })));
        add(t('Go on'), withOutcome(entries, 'PASSED').map(entry => ({name: nameOf(entry), detail: entry.member?.email, ...guestOf(entry.member)})));
    } else if (type === NodeType.IF_CONDITION) {
        add(t('Yes'), withOutcome(entries, 'YES').map(entry => ({name: nameOf(entry), detail: entry.member?.email, ...guestOf(entry.member)})));
        add(t('No'), withOutcome(entries, 'NO').map(entry => ({name: nameOf(entry), detail: entry.member?.email, ...guestOf(entry.member)})));
    } else if (isSendStep(type)) {
        const sent = withOutcome(entries, 'SENT');
        add(
            type === NodeType.SEND_PUSH ? t('Would get this push') : t('Would get this email'),
            sent.flatMap(entry => entry.member
                ? [{name: nameOf(entry), detail: type === NodeType.SEND_PUSH ? undefined : entry.step.recipients?.join(', '), ...guestOf(entry.member)}]
                // A send the run makes once names its addresses, not a guest.
                : (entry.step.recipients ?? []).map(recipient => ({name: recipient}))),
            sent.reduce((sum, entry) => sum + (entry.member ? 1 : entry.step.recipients?.length ?? 0), 0)
        );
        add(t('Skipped'), withOutcome(entries, 'NOT_SENT').map(entry => ({
            name: entry.member ? nameOf(entry) : label,
            detail: reasonText(entry.step.reason, t),
            problem: true,
            ...guestOf(entry.member),
        })));
    } else if (type !== NodeType.WAIT && type !== NodeType.AFTER_EVERYONE) {
        add(t('Go on'), withOutcome(entries, 'PASSED')
            .filter(entry => entry.member)
            .map(entry => ({name: nameOf(entry), detail: entry.member?.email, ...guestOf(entry.member)})));
    }

    add(t('Would fail here'), withOutcome(entries, 'FAILED').map(entry => ({
        name: entry.member ? nameOf(entry) : label,
        detail: reasonText(entry.step.reason, t),
        problem: true,
        ...guestOf(entry.member),
    })));

    let note: string | null = null;
    if (type === NodeType.WAIT) note = t('Preview wait note', {duration: formatWaitDuration(config, t) ?? ''});
    else if (type === NodeType.AFTER_EVERYONE) note = t('Everything below runs once, after all members.');
    else {
        const notPreviewed = withOutcome(entries, 'NOT_PREVIEWED')[0];
        if (notPreviewed) note = reasonText(notPreviewed.step.reason, t);
    }

    return {lists, note};
};
